import { Injectable, DestroyRef, EnvironmentInjector, inject, runInInjectionContext } from '@angular/core';
import { environment } from '../../environments/environment';
import { Observable } from 'rxjs';
import { filter, first, tap } from 'rxjs/operators';
import {
  Firestore,
  DocumentReference,
  DocumentSnapshot,
  DocumentData,
  Query,
  QuerySnapshot,
  CollectionReference,
  docData as _docData,
  collectionData as _collectionData,
  getDoc as _getDoc,
  getDocs as _getDocs,
  getDocsFromServer as _getDocsFromServer,
  setDoc,
  doc,
  Timestamp,
} from '@angular/fire/firestore';
import {TenantService} from './tenant.service';
import {Store, select} from '@ngrx/store';
import {selectTenantInfo} from '../store/selectors';
import { Auth } from '@angular/fire/auth';

interface PathStats {
  reads: number;
  emissions: number;
  activeListeners: number;
}

@Injectable({ providedIn: 'root' })
export class FirestoreService {

  readonly db = inject(Firestore);
  readonly tenantService = inject(TenantService);
  private readonly afAuth = inject(Auth);
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(EnvironmentInjector);

  private stats = new Map<string, PathStats>();
  private _loggingEnabled = !!environment.tracking?.firestoreLogging;

  private get loggingEnabled(): boolean {
    return this._loggingEnabled;
  }

  constructor() {
    this.store.pipe(
      select(selectTenantInfo),
      filter(tenant => !!tenant),
      first(),
    ).subscribe(tenant => {
      this._loggingEnabled = !!environment.tracking?.firestoreLogging
        || !!(tenant as any)?.firestoreLogging;
    });

    this.tenantService.tenantId$.pipe(first()).subscribe(tenantId => {
      const intervalId = setInterval(() => this.flushStats(tenantId), 20_000);
      setTimeout(() => this.flushStats(tenantId), 5_000);
      this.destroyRef.onDestroy(() => clearInterval(intervalId));
    });
  }

  private flushStats(tenantId: string): void {
    if (!this.loggingEnabled || this.stats.size === 0) return;
    // const payload = {
    //   tenantId,
    //   userId: this.afAuth.currentUser?.uid ?? 'anonymous',
    //   updatedAt: Timestamp.now(),
    //   stats: Object.fromEntries(this.stats),
    // };
    // setDoc(doc(this.db, `firestoreReadStats/${tenantId}`), payload, { merge: true })
    //   .catch(err => console.warn('[FS] flushStats write failed', err));
  }

  // ---------------------------------------------------------------------------
  // Reactive reads (live listeners)
  // ---------------------------------------------------------------------------

  docData<T = DocumentData>(ref: DocumentReference, options?: { idField?: string }): Observable<T> {
    const path = ref.path;
    return runInInjectionContext(this.injector, () => _docData(ref, options as any) as Observable<T>).pipe(
      this.trackListener(path),
      tap(() => this.countRead(path, 1)),
    );
  }

  collectionData<T>(
    q: Query | CollectionReference,
    options?: { idField?: string },
  ): Observable<T[]> {
    const path = this.pathFromQuery(q);
    return runInInjectionContext(this.injector, () => _collectionData(q as Query, options as any) as Observable<T[]>).pipe(
      this.trackListener(path),
      tap(docs => this.countRead(path, docs.length)),
    );
  }

  // ---------------------------------------------------------------------------
  // One-shot reads
  // ---------------------------------------------------------------------------

  getDoc(ref: DocumentReference): Promise<DocumentSnapshot> {
    const path = ref.path;
    return runInInjectionContext(this.injector, () => _getDoc(ref)).then(snap => {
      this.countRead(path, snap.exists() ? 1 : 0);
      return snap;
    });
  }

  getDocs(q: Query | CollectionReference): Promise<QuerySnapshot> {
    const path = this.pathFromQuery(q);
    return runInInjectionContext(this.injector, () => _getDocs(q as Query)).then(snap => {
      this.countRead(path, snap.size);
      return snap;
    });
  }

  getDocsFromServer(q: Query | CollectionReference): Promise<QuerySnapshot> {
    const path = this.pathFromQuery(q);
    return runInInjectionContext(this.injector, () => _getDocsFromServer(q as Query)).then(snap => {
      this.countRead(path, snap.size);
      return snap;
    });
  }

  // ---------------------------------------------------------------------------
  // DevTools
  // ---------------------------------------------------------------------------

  report(): void {
    const rows = Array.from(this.stats.entries())
      .sort(([, a], [, b]) => b.reads - a.reads)
      .reduce(
        (acc, [key, val]) => ({
          ...acc,
          [key]: {
            reads: val.reads,
            emissions: val.emissions,
            activeListeners: val.activeListeners,
          },
        }),
        {} as Record<string, PathStats>,
      );
    console.table(rows);
  }

  resetStats(): void {
    this.stats.clear();
  }

  // ---------------------------------------------------------------------------
  // Internals
  // ---------------------------------------------------------------------------

  private trackListener<T>(path: string) {
    return (source: Observable<T>) =>
      new Observable<T>(observer => {
        this.openListener(path);
        const sub = source.subscribe(observer);
        return () => {
          this.closeListener(path);
          sub.unsubscribe();
        };
      });
  }

  private openListener(path: string) {
    if (!this.loggingEnabled) return;
    const s = this.getOrCreate(path);
    s.activeListeners++;
  }

  private closeListener(path: string) {
    if (!this.loggingEnabled) return;
    const s = this.getOrCreate(path);
    s.activeListeners = Math.max(0, s.activeListeners - 1);
  }

  private countRead(path: string, n: number) {
    if (!this.loggingEnabled) return;
    const s = this.getOrCreate(path);
    s.reads += n;
    s.emissions++;
  }

  private getOrCreate(path: string): PathStats {
    let s = this.stats.get(path);
    if (!s) {
      s = { reads: 0, emissions: 0, activeListeners: 0 };
      this.stats.set(path, s);
    }
    return s;
  }

  private pathFromQuery(q: Query | CollectionReference): string {
    return (
      (q as any)._query?.path?.segments?.join('/') ??
      (q as any).path ??
      'unknown'
    );
  }

}
